import { useState } from "react";
import { useMediaTranslation } from "../../../hooks/useMediaTranslation";
import { buildEvidenceOverlayTitle } from "../../../services/evidenceLabels";
import { downloadEvidenceWithTranslation } from "../../../services/media/mediaDownloadService";
import type { IntervieweeMediaOverlayProps, MediaPanelProps } from "../types";

function MediaPanel({ title, url, mimeType, label, fallback, html }: MediaPanelProps) {
  const mediaKind = mimeType?.split("/")[0] ?? "document";

  return (
    <div className="media-preview-panel">
      <p className="media-preview-panel__title">{title}</p>
      {html ? (
        <div className="media-preview-html" dangerouslySetInnerHTML={{ __html: html }} />
      ) : url ? (
        <div className="media-preview-media">
          {mediaKind === "image" ? (
            <img src={url} alt={label} />
          ) : mediaKind === "audio" ? (
            <audio controls preload="metadata" src={url} />
          ) : mediaKind === "video" ? (
            <video controls preload="metadata" src={url} />
          ) : (
            <iframe src={url} title={label} />
          )}
        </div>
      ) : (
        <div className="media-preview-empty">{fallback}</div>
      )}
    </div>
  );
}

export function IntervieweeMediaOverlay({ activePreview, onClose }: IntervieweeMediaOverlayProps) {
  const { evidence, preview } = activePreview;
  const overlayTitle = buildEvidenceOverlayTitle(evidence);
  const translationQuery = useMediaTranslation(evidence.assetId);
  const translation = translationQuery.data?.translation ?? { kind: "none" };
  const [isDownloading, setIsDownloading] = useState(false);

  const hasTranslation = translationQuery.isLoading || translation.kind !== "none";
  const translationMimeType = translation.kind === "file"
    ? translation.mimeType
    : translation.kind === "mixed"
      ? translation.file.mimeType
      : "text/plain";

  const handleDownload = () => {
    if (isDownloading) {
      return;
    }
    setIsDownloading(true);
    void downloadEvidenceWithTranslation(evidence, hasTranslation ? translation : null)
      .finally(() => setIsDownloading(false));
  };

  return (
    <div
      className="media-preview-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="media-preview-dialog">
        {overlayTitle ? <p className="small-meta media-preview-overlay__title">{overlayTitle}</p> : null}
        <div className="media-preview-actions">
          <button
            type="button"
            className="media-preview-action"
            onClick={handleDownload}
            aria-label="Download media"
            disabled={isDownloading || translationQuery.isLoading}
          >
            <svg viewBox="0 0 24 24" role="presentation">
              <path d="M12 4v10m0 0l-4-4m4 4l4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 20h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <button type="button" className="media-preview-action" onClick={onClose} aria-label="Close preview">
            <svg viewBox="0 0 24 24" role="presentation">
              <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className={`media-preview-grid${hasTranslation ? " is-two-col" : ""}`}>
          <MediaPanel
            title="Original"
            url={preview?.objectUrl ?? null}
            mimeType={evidence.mimeType ?? preview?.mimeType ?? null}
            label={preview?.label ?? "Original media"}
            fallback="Original media is still loading."
          />
          {hasTranslation ? (
            translationQuery.isLoading ? (
              <MediaPanel
                title="Translation"
                url={null}
                mimeType="text/plain"
                label="Translation"
                fallback="Loading translation..."
              />
            ) : translation.kind === "mixed" ? (
              <div className="media-preview-column">
                <MediaPanel
                  title="Translation Image"
                  url={translation.file.url}
                  mimeType={translation.file.mimeType}
                  label="Translation image"
                  fallback="Translation file is unavailable."
                />
                <MediaPanel
                  title="Translation Text"
                  url={null}
                  mimeType="text/plain"
                  label="Translation text"
                  fallback=""
                  html={translation.html}
                />
              </div>
            ) : translation.kind === "html" ? (
              <MediaPanel
                title="Translation"
                url={null}
                mimeType={translationMimeType}
                label="Translation"
                fallback=""
                html={translation.html}
              />
            ) : translation.kind === "file" ? (
              <MediaPanel
                title="Translation"
                url={translation.url}
                mimeType={translationMimeType}
                label="Translation"
                fallback="Translation file is unavailable."
              />
            ) : (
              <MediaPanel
                title="Translation"
                url={null}
                mimeType={translationMimeType}
                label="Translation"
                fallback="Translation is not available."
              />
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
